import {useEffect, useState} from 'react';
import {GetAllDepartments} from '../../../../../reduxToolkit/DocumentPanel/DocumentPanelApi';
import {
    CreateShift,
    GetAllCafeterias,
    GetShiftById,
    UpdateShift
} from '../../../../../reduxToolkit/FoodieShift/FoodieShiftApi';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {useParamsContextProvider} from '../../../../../hooks/ParamsContextProvider';
import {useTranslation} from 'react-i18next';
import {MODEL_CONTEXT} from '../../Model/Models';
import routes from '../../../../../Routes/routes';
import HandleChange from '../../../../../Helpers/HandleChange';
import {toast} from 'react-toastify';
import {permissionObj} from '../../../../../Helpers/permission';
import {errorMsg, userListToLabelEmailPhoneValue} from '../../../../../Apis/functions';
import {
    PersonChipBlack
} from "../../../../../components/buttons/IconButtons";

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @returns {{paramsContext, item: {name: string, id: null}, cafeteriasOptions: (*|*[]), setCafeterias: (value: (((prevState: *[]) => *[]) | *[])) => void, setDisabled: (value: unknown) => void, permission: *, setDepartments: (value: (((prevState: *[]) => *[]) | *[])) => void, cafeterias: *[], departmentsOptions: (*|*[]), emptyFields: unknown, handleAddEdit: handleAddEdit, t: TFunction<"translation", undefined>, DEV: *, setEmptyFields: (value: unknown) => void, handleChange: (function(*=, *=, *=, *=): void), disabled: unknown, id, userOptions: (*|*[]), departments: *[], setUser: (value: (((prevState: *[]) => *[]) | *[])) => void, user: *[], setItem: (value: (((prevState: {name: string, id: null}) => {name: string, id: null}) | {name: string, id: null})) => void}}
 * @constructor
 */
const AddEditShiftApp = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { paramsContext, setParamsContext } = useParamsContextProvider();
    const { allCafeterias } = useSelector(state => state.FoodieShiftSlice);
    const { getAllDepartments } = useSelector((state) => state.DocumentPanelSlice);


    const { t } = useTranslation();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [item, setItem] = useState({id: null, name: ''});
    const [disabled, setDisabled] = useState(null);
    const [emptyFields, setEmptyFields] = useState(null);

    const [cleanChip, setCleanChip] = useState(false);

    const [cafeterias, setCafeterias] = useState([]);
    const [user, setUser] = useState([]);
    const [departments, setDepartments] = useState([]);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [modified, setModified] = useState(false);


    const cafeteriasOptions = allCafeterias?.map((item) => ({id: item.id, label: item.name})) || [];
    const departmentsOptions = getAllDepartments?.map((item) => ({id: item.id, label: item.name})) || [];

    /**
     * Fetches the initial values such as the list of departments, users, cafeterias, and sets the context.
     */
    useEffect(()=> {
        if(setParamsContext){
            setParamsContext({...MODEL_CONTEXT});
        }
        dispatch(GetAllDepartments());
        dispatch(GetAllCafeterias());
    },[])

    /**
     * Sets the values obtained from meals for the list of cafeterias, users, and departments.
     */
    useEffect(()=> {
        if(id){
            getData();
        }
    },[id]);

    useEffect(()=> {
        if(modified){
            getData();
            setModified(false);
        }
    },[modified]);

    const getData = () => {
        dispatch(GetShiftById(id)).then(({ payload: { data: { data, code } } }) => {
            setItem({id: data.id, name: data.name} );
            setCafeterias(userListToLabelEmailPhoneValue(data.foodieCafeterias, 'id', false, undefined));
            setUser(userListToLabelEmailPhoneValue(data.users, "userId", true, PersonChipBlack));
            setDepartments(userListToLabelEmailPhoneValue(data.departments, 'id', false, undefined));
        });
    }

    /**
     * Validates the view based on permissions; if not authorized, returns a forbidden message.
     */
    useEffect(() => {
        if (DEV) return;

        if ((!id && !permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_CREATE)) || (id && !permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_UPDATE))) {
            navigate(routes.forbidden);
        }
    }, [DEV, permission, id]);

    /**
     * Function to create or update a shift.
     */
    const handleAddEdit = () => {
        if(item.name == '' || cafeterias.length <= 0){
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields').toUpperCase());
            return;
        }
        let body = {
            id: item.id,
            name: item.name,
            foodieCafeteriaIds: cafeterias.map(item => item.id),
            departmentIds: departments.map(item => item.id),
            userIds: user.map(item => item.id)
        }
        dispatch(item?.id ? UpdateShift(body) : CreateShift(body)).then(({ payload: { data: { success, data, code } } }) => {
            if (success && data.id){
                if(item?.id){
                    setModified(true);
                    toast.success(t('has_been_updated_successfully'));
                }else {
                    toast.success(t('has_been_created_successfully'));
                    navigate(routes.foodie.list)
                }
            } else errorMsg(code)
        });
    }

    const handleClearFields = () => {
        setItem({id: null, name: ''});
        setCafeterias([]);
        setUser([]);
        setDepartments([]);
        setCleanChip(true);
    }

    return {
        id,
        t,
        DEV,
        permission,
        item, setItem,
        disabled, setDisabled,
        emptyFields, setEmptyFields,
        handleChange,
        cafeteriasOptions,
        departmentsOptions,
        cafeterias, setCafeterias,
        user, setUser,
        departments, setDepartments,
        paramsContext,
        handleAddEdit,
        handleClearFields,
        cleanChip,
        setCleanChip
    }

}

export default AddEditShiftApp;
